import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  type TestCaseAttribute,
  type ExecutionStrategy,
} from 'src/components/pipelines/components/test-management/types';
import envBaseUrl from 'src/components/pipelines/utils/env-base-url';
import { useFlag } from 'src/hooks/flag';
import { getCurrentRepositoryFullSlug } from 'src/selectors/repository-selectors';
import { useMutation } from 'src/utils/fb-query';
import { authRequest, jsonHeaders } from 'src/utils/fetch';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { messages } from '../messages';

type TestUidsArgs = {
  testUuids: string[];
};

type BulkUpdateAttributesArgs = TestUidsArgs & {
  attributes: TestCaseAttribute[];
};

type BulkUpdateExecutionStrategyArgs = TestUidsArgs & {
  executionStrategy: ExecutionStrategy;
};

type TestUuidsBody = {
  test_uuids: string[];
};

type BulkUpdateTestCasesBody = TestUuidsBody & {
  attributes?: TestCaseAttribute[];
  executionStrategy?: ExecutionStrategy;
};

const buildTestsUrl = (repositoryFullSlug: string) =>
  `${envBaseUrl('/!api/internal')}/repositories/${repositoryFullSlug}/tests`;

const updateTestCases = async (
  repositoryFullSlug: string,
  body: BulkUpdateTestCasesBody
) => {
  const response = await fetch(
    authRequest(buildTestsUrl(repositoryFullSlug), {
      method: 'PUT',
      headers: jsonHeaders,
      body: JSON.stringify(body),
    })
  );

  if (!response.ok) {
    throw new Error(
      `Failed to update test cases: ${response.status} ${response.statusText}`
    );
  }
};

const deleteTestCases = async (
  repositoryFullSlug: string,
  body: TestUuidsBody
) => {
  const response = await fetch(
    authRequest(buildTestsUrl(repositoryFullSlug), {
      method: 'DELETE',
      headers: jsonHeaders,
      body: JSON.stringify(body),
    })
  );

  if (!response.ok) {
    throw new Error(
      `Failed to delete test cases: ${response.status} ${response.statusText}`
    );
  }
};

type Options = {
  onSuccess?: () => void;
};

export const useBulkTestCaseActions = ({ onSuccess }: Options = {}) => {
  const { showSuccessFlag, showErrorFlag } = useFlag();
  const { formatMessage } = useIntl();
  const repositoryFullSlug = useSelector(getCurrentRepositoryFullSlug);

  const bulkUpdateAttributesFn = useCallback(
    async ({ testUuids, attributes }: BulkUpdateAttributesArgs) => {
      if (!repositoryFullSlug) {
        throw new Error('Repository path is not available');
      }
      if (testUuids.length === 0) {
        return 0;
      }
      await updateTestCases(repositoryFullSlug, {
        test_uuids: testUuids,
        attributes,
      });
      return testUuids.length;
    },
    [repositoryFullSlug]
  );

  const bulkUpdateExecutionStrategyFn = useCallback(
    async ({
      testUuids,
      executionStrategy,
    }: BulkUpdateExecutionStrategyArgs) => {
      if (!repositoryFullSlug) {
        throw new Error('Repository path is not available');
      }
      if (testUuids.length === 0) {
        return 0;
      }
      await updateTestCases(repositoryFullSlug, {
        test_uuids: testUuids,
        executionStrategy,
      });
      return testUuids.length;
    },
    [repositoryFullSlug]
  );

  const bulkDeleteFn = useCallback(
    async ({ testUuids }: TestUidsArgs) => {
      if (!repositoryFullSlug) {
        throw new Error('Repository path is not available');
      }
      if (testUuids.length === 0) {
        return 0;
      }
      await deleteTestCases(repositoryFullSlug, {
        test_uuids: testUuids,
      });
      return testUuids.length;
    },
    [repositoryFullSlug]
  );

  const updateAttributes = useMutation(bulkUpdateAttributesFn, {
    onSuccess: count => {
      onSuccess?.();
      showSuccessFlag(
        formatMessage(messages.bulkUpdateAttributesSuccessBody, {
          count: count ?? 0,
        }),
        formatMessage(messages.bulkUpdateAttributesSuccessTitle, {
          count: count ?? 0,
        })
      );
    },
    onError: (error: Error) => {
      captureExceptionWithTags(error, {
        section: 'pipelines-test-management',
        action: 'bulk-update-attributes',
      });
      showErrorFlag(
        formatMessage(messages.bulkUpdateAttributesErrorBody),
        formatMessage(messages.bulkUpdateAttributesErrorTitle)
      );
    },
  });

  const updateExecutionStrategy = useMutation(bulkUpdateExecutionStrategyFn, {
    onSuccess: count => {
      onSuccess?.();
      showSuccessFlag(
        formatMessage(messages.bulkUpdateAttributesSuccessBody, {
          count: count ?? 0,
        }),
        formatMessage(messages.bulkUpdateAttributesSuccessTitle, {
          count: count ?? 0,
        })
      );
    },
    onError: (error: Error) => {
      captureExceptionWithTags(error, {
        section: 'pipelines-test-management',
        action: 'bulk-update-execution-strategy',
      });
      showErrorFlag(
        formatMessage(messages.bulkUpdateAttributesErrorBody),
        formatMessage(messages.bulkUpdateAttributesErrorTitle)
      );
    },
  });

  const remove = useMutation(bulkDeleteFn, {
    onSuccess: count => {
      onSuccess?.();
      showSuccessFlag(
        formatMessage(messages.bulkRemoveTestsSuccessBody, {
          count: count ?? 0,
        }),
        formatMessage(messages.bulkRemoveTestsSuccessTitle, {
          count: count ?? 0,
        })
      );
    },
    onError: (error: Error) => {
      captureExceptionWithTags(error, {
        section: 'pipelines-test-management',
        action: 'bulk-delete-test-cases',
      });
      showErrorFlag(
        formatMessage(messages.bulkRemoveTestsErrorBody),
        formatMessage(messages.bulkRemoveTestsErrorTitle)
      );
    },
  });

  const isPending =
    updateAttributes.isPending ||
    updateExecutionStrategy.isPending ||
    remove.isPending;

  return useMemo(
    () => ({
      bulkUpdateAttributes: updateAttributes.mutate,
      bulkUpdateExecutionStrategy: updateExecutionStrategy.mutate,
      bulkDeleteTestCases: remove.mutate,
      isPending,
    }),
    [
      updateAttributes.mutate,
      updateExecutionStrategy.mutate,
      remove.mutate,
      isPending,
    ]
  );
};
